import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { getAllDetails, postAllData } from "../../service/ProductService";
import {
  CHANGE_CASH_STATUS,
  GET_BOOKING_DETAIL,
} from "../../config/ApiConstant";
import AppBreadcrumb from "../../components/CustomeBreadcrumb/AppBreadcrumb";
import Loader from "../../components/Loadeer/Loader";
import { Column } from "primereact/column";
import { Rating } from "primereact/rating";
import { Avatar } from "primereact/avatar";
import { DataTable } from "primereact/datatable";
import { Image } from "primereact/image";
import { Badge } from "primereact/badge";
import { getBookingStatusSeverity } from "../../appconfig/AppHelper";

const BookingDetail = () => {
  document.title = "Booking Detail | Agriown";

  const { id }: any = useParams();
  const booking_id = atob(id);
  const toast = useRef<any>(null);

  // booking detail state
  const [bookingDetail, setBookingDetail] = useState<any>(null);

  // loading state
  const [loading, setLoading] = useState<boolean>(false);

  // confirm dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  // fetch booking detail
  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await getAllDetails(
        `${GET_BOOKING_DETAIL}/${booking_id}`
      );
      if (response) setBookingDetail(response);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  // useeffect fetch booking detail
  useEffect(() => {
    fetchBooking();
  }, [booking_id]);

  // open confirmation dialog
  const handleOpenConfirmDialog = () => {
    setShowConfirmDialog(true);
  };

  // confirm action and api call for payment status cash
  const handleConfirm = async () => {
    try {
      setLoading(true);

      // find payment where method is Cash
      const cashPayment = bookingDetail?.payment_data?.find(
        (p: any) => p.payment_method === "Cash"
      );
      const payload = {
        payment_id: cashPayment?.id,
      };

      const response = await postAllData(CHANGE_CASH_STATUS, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Success",
          detail: response.message,
        });
        setShowConfirmDialog(false);
        fetchBooking();
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error?.response?.data?.message,
      });
      setShowConfirmDialog(false);
    } finally {
      setLoading(false);
    }
  };

  // ----- prices details  -----
  const serviceCharge = !window.cn(bookingDetail?.service_charge)
    ? bookingDetail?.service_charge
    : 0;

  // calc total areas
  const totalAcres = !window.cn(bookingDetail?.booking_farm_data)
    ? bookingDetail.booking_farm_data.reduce((sum: number, farm: any) => {
      const farmAcre = !window.cn(farm.booking_farm_crop_data)
        ? farm.booking_farm_crop_data.reduce(
          (s: number, crop: any) => s + (Number(crop.area) || 0),
          0
        )
        : 0;
      return sum + farmAcre;
    }, 0)
    : 0;

  const servicePrice = !window.cn(bookingDetail?.service_price)
    ? Number(bookingDetail?.service_price)
    : 0;

  const calculatedServiceCharge = serviceCharge || servicePrice * totalAcres;

  const otherCharge = !window.cn(bookingDetail?.other_charge)
    ? bookingDetail?.other_charge
    : 0;

  const taxRate = !window.cn(bookingDetail?.tax_rate)
    ? bookingDetail?.tax_rate
    : 0;

  const taxAmount = !window.cn(bookingDetail?.tax_amount)
    ? bookingDetail?.tax_amount
    : 0;

  const grandTotal = !window.cn(bookingDetail?.total_amount)
    ? bookingDetail?.total_amount
    : serviceCharge +
    otherCharge +
    taxAmount -
    (bookingDetail?.discount_amount || 0);

  const paidAmount = !window.cn(bookingDetail?.payment_data)
    ? bookingDetail?.payment_data?.reduce(
      (sum: number, p: any) => sum + (p.amount_paid || 0),
      0
    )
    : 0;

  const remainingAmount = grandTotal - paidAmount;

  // status badge for booking status
  const renderStatusBadge = (status?: string) => {
    return <Badge value={status} severity={getBookingStatusSeverity(status)} />;
  };

  // open document in new tab
  const handleOpenDocument = (link: any) => {
    if (link) {
      window.open(link, "_blank");
    } else {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No document URL found",
      });
    }
  };

  if (loading) {
    return <Loader />;
  }
  let SoilName = bookingDetail?.service_name === "Soil Testing";
  let PDF = window.cn(bookingDetail?.soil_test_pdf);

  return (
    <>
      <AppBreadcrumb
        currentMenu="Booking"
        pageName="Booking Detail"
        pageTitle={
          <div className="flex align-items-center justify-content-between w-full">
            <div className="flex align-items-center gap-3">
              <span>Booking Detail</span>
              {renderStatusBadge(bookingDetail?.booking_status)}
            </div>

            {SoilName && (
              <Button
                label="Download Report"
                icon="pi pi-download"
                className="p-button-sm ml-2"
                disabled={PDF}
                onClick={() => handleOpenDocument(bookingDetail?.soil_test_pdf)}
              />
            )}
          </div>
        }
        goToPage={true}
      />
      <Toast ref={toast} />

      <div className="grid booking-detail">
        {/* Left Side */}
        <div className="col-12 lg:col-8">
          <div className="grid">
            <div className="col-12 lg:col-6">
              {/* farmer detail */}
              <Card title="Farmer Detail" className="p-0 bg-white detail-card">
                <div className="detail-row">
                  Name
                  <span>
                    {!window.cn(bookingDetail?.farmer_name)
                      ? bookingDetail?.farmer_name
                      : "-"}
                  </span>
                </div>
                <div className="detail-row">
                  Mobile
                  <span>
                    {!window.cn(bookingDetail?.farmer_mobile)
                      ? bookingDetail?.farmer_mobile
                      : "-"}
                  </span>
                </div>
              </Card>
            </div>
            <div className="col-12 lg:col-6">
              {/* operator detail */}
              <Card
                title="Operator Detail"
                className="p-0 bg-white detail-card"
              >
                <div className="detail-row mb-3">
                  Operator Charge
                  <span>
                    {!window.cn(bookingDetail?.operator_charge)
                      ? `₹${bookingDetail?.operator_charge}`
                      : "-"}
                  </span>
                </div>
                <DataTable
                  value={
                    !window.cn(bookingDetail?.assign_booking_operator)
                      ? bookingDetail?.assign_booking_operator
                      : []
                  }
                  stripedRows
                  showGridlines
                  className="datatable-responsive p-datatble-sm"
                  emptyMessage="No Items Found"
                >
                  <Column field="operator_name" header="Name" />
                  <Column
                    field="status"
                    body={(rowData) => {
                      return (
                        <Badge
                          severity={
                            rowData.status.toLowerCase() === "pending"
                              ? "warning"
                              : rowData.status.toLowerCase() === "rejected"
                                ? "danger"
                                : "success"
                          }
                          value={rowData.status}
                        ></Badge>
                      );
                    }}
                    header="Status"
                  />
                </DataTable>
              </Card>
            </div>
            <div className="col-12">
              {/* booking details */}
              <Card
                title="Service Details"
                className="p-0 bg-white detail-card"
              >
                <div className="detail-row">
                  <span>Service</span>
                  <span>
                    {!window.cn(bookingDetail?.service_name)
                      ? bookingDetail?.service_name
                      : "-"}
                  </span>
                </div>

                <div className="farm-details">
                  <h6 className="mt-4 farm-head mb-2">Farm Details</h6>
                  <Accordion multiple>
                    {!window.cn(bookingDetail?.booking_farm_data) ? (
                      bookingDetail.booking_farm_data.map(
                        (farm: any, farmIndex: number) => (
                          <AccordionTab
                            key={farmIndex}
                            header={
                              <>
                                {/* Farm Header */}
                                <div className="flex gap-2 align-items-center">
                                  <Tag
                                    value={`#${farmIndex + 1}`}
                                    severity="info"
                                  />
                                  <div className="flex align-items-center gap-1">
                                    <i className="pi pi-map-marker text-primary text-lg"></i>
                                    <h5 className="m-0 text-primary font-semibold">
                                      {farm.farm_name}
                                    </h5>
                                  </div>
                                </div>
                              </>
                            }
                          >
                            {/* Crops under this Farm */}
                            {!window.cn(farm.booking_farm_crop_data) ? (
                              <div className="flex flex-column gap-3">
                                {farm.booking_farm_crop_data.map(
                                  (crop: any, cropIndex: number) => (
                                    <div
                                      key={crop.id || cropIndex}
                                      className="p-3 border-1 surface-border border-round-lg bg-gray-50 crop-section"
                                    >
                                      {/* Crop Info */}
                                      {!window.cn(
                                        bookingDetail?.service_name
                                      ) &&
                                        bookingDetail?.service_name !==
                                        "Soil Testing" && (
                                          <div className="grid text-sm mb-2">
                                            <div className="col-12 md:col-3">
                                              <strong>Crop:</strong>{" "}
                                              {crop.crop_name || "-"}
                                            </div>
                                            <div className="col-12 md:col-3">
                                              <strong>Variety:</strong>{" "}
                                              {crop.variety_name || "-"}
                                            </div>
                                            <div className="col-12 md:col-3">
                                              <strong>Area:</strong>{" "}
                                              {crop.area || "-"}
                                            </div>
                                            <div className="col-12 md:col-3">
                                              <strong>Crop Age:</strong>{" "}
                                              {crop.sowing_date || "-"}
                                            </div>
                                            <div className="col-12 md:col-3">
                                              <strong>
                                                Spray For Problem:
                                              </strong>{" "}
                                              {crop.spray_for_problem || "-"}
                                            </div>
                                          </div>
                                        )}

                                      {/* Items List */}
                                      {!window.cn(
                                        crop.booking_service_item_data
                                      ) && (
                                          <div className="mt-2">
                                            <DataTable
                                              value={
                                                crop.booking_service_item_data
                                              }
                                              showGridlines
                                              stripedRows
                                              className="p-datatable-sm"
                                              emptyMessage="No items found"
                                            >
                                              <Column
                                                header="Item Name"
                                                body={(rowData) =>
                                                  rowData.service_item_master_data
                                                    ?.item_name || "-"
                                                }
                                              />
                                              <Column
                                                header="Type"
                                                body={(rowData) =>
                                                  rowData.service_item_master_data
                                                    ?.item_type || "-"
                                                }
                                              />
                                              {/* <Column
                                              header="Amount (₹)"
                                              field="amount"
                                              body={(rowData) => rowData.amount}
                                            /> */}
                                            </DataTable>
                                          </div>
                                        )}
                                    </div>
                                  )
                                )}
                              </div>
                            ) : (
                              <p className="text-center text-500 mt-3">
                                No Crop Data Found
                              </p>
                            )}
                          </AccordionTab>
                        )
                      )
                    ) : (
                      <p className="text-center text-500">No Farm Data Found</p>
                    )}
                  </Accordion>
                </div>
              </Card>
            </div>

            {/* form 8a / aadhar card */}
            <div className="col-12 lg:col-6">
              <Card title="Documents" className="p-2 bg-white detail-card">
                {!window.cn(bookingDetail?.form_8a) ||
                  !window.cn(bookingDetail?.aadhar_card) ? (
                  <div className="flex flex-column md:flex-row gap-3 justify-content-center">
                    {!window.cn(bookingDetail?.form_8a) && (
                      <div className="flex-1 text-center">
                        <h6 className="mb-2">Form 8A</h6>
                        <Button
                          tooltip="Open Document"
                          tooltipOptions={{ position: "top" }}
                          icon="pi pi-file"
                          color="primary"
                          onClick={() =>
                            handleOpenDocument(bookingDetail?.form_8a)
                          }
                        />
                      </div>
                    )}
                    {!window.cn(bookingDetail?.aadhar_card) && (
                      <div className="flex-1 text-center">
                        <h6 className="mb-2">Aadhar Card</h6>
                        <Button
                          tooltip="Open Document"
                          tooltipOptions={{ position: "top" }}
                          icon="pi pi-file"
                          color="primary"
                          onClick={() =>
                            handleOpenDocument(bookingDetail?.aadhar_card)
                          }
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-500">No Document Found</p>
                )}
              </Card>
            </div>

            {/* Before / After Images */}
            <div className="col-12 lg:col-6">
              <Card
                title="Before / After Images"
                className="p-2 bg-white detail-card"
              >
                {!window.cn(bookingDetail?.after_img) ||
                  !window.cn(bookingDetail?.before_img) ? (
                  <div className="flex flex-column md:flex-row gap-3 justify-content-center">
                    {!window.cn(bookingDetail?.before_img) && (
                      <div className="flex-1 text-center">
                        <h6 className="mb-2">Before</h6>
                        <Image
                          src={bookingDetail?.before_img}
                          alt="Before"
                          preview
                          className="flex border-round shadow-2"
                          width="250"
                        />
                      </div>
                    )}
                    {!window.cn(bookingDetail?.after_img) && (
                      <div className="flex-1 text-center">
                        <h6 className="mb-2">After</h6>
                        <Image
                          preview
                          src={bookingDetail?.after_img}
                          alt="After"
                          className="flex border-round shadow-2"
                          width="250"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-500">No Document Found</p>
                )}
              </Card>
            </div>

            <div className="col-12 lg:col-6">
              {/* Review Detail */}
              <Card title="Reviews" className="p-0 bg-white detail-card">
                {!window.cn(bookingDetail?.review_data) ? (
                  <div className="flex flex-column gap-3">
                    {bookingDetail?.review_data?.map((review: any) => (
                      <div
                        key={review.id}
                        // className="border-1 border-round p-3 shadow-1 flex flex-column gap-2 bg-gray-50"
                        className="border-round p-3 shadow-3 hover:shadow-4 flex flex-column gap-2 bg-[#e1ebf540]"
                      >
                        {/* Header with Avatar & Names */}
                        <div className="flex align-items-center justify-content-between">
                          <div className="flex align-items-center gap-3">
                            <Avatar
                              label={review.review_by_name
                                ?.charAt(0)
                                .toUpperCase()}
                              shape="circle"
                              size="large"
                              style={{ background: "#16a34a", color: "#fff" }}
                            />
                            <div>
                              <h6 className="m-0">
                                By: {review.review_by_name}
                              </h6>
                              <small className="text-500">
                                For: {review.review_for_name}
                              </small>
                            </div>
                          </div>
                          <Tag
                            value={review.type}
                            severity={
                              review.type === "Farmer" ? "success" : "info"
                            }
                            className="px-2  py-1"
                          />
                        </div>

                        {/* Rating */}
                        <div className="flex align-items-center gap-2">
                          <Rating
                            value={review.ratings}
                            readOnly
                            stars={5}
                            cancel={false}
                          />
                          <small className="text-600">
                            ({review.ratings}/5)
                          </small>
                        </div>

                        {/* Message */}
                        <p className="m-0 text-700">{review.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-500">No Reviews Found</p>
                )}
              </Card>
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="col-12 lg:col-4">
          {/* summary  */}
          <Card title="Summary" className="p-0 mb-3 bg-white detail-card">
            <div className="border-none detail-row">
              <span>
                Service Charge
                <small className="text-500 ml-2">
                  (service price ₹{servicePrice}) <br /> × (total area{" "}
                  {totalAcres} )
                </small>
              </span>
              <span>₹{calculatedServiceCharge}</span>
            </div>

            <div className="border-none detail-row">
              <span>Other Charges</span>
              <span>₹{otherCharge}</span>
            </div>
            <div className="border-none detail-row">
              <span>Redeem Amount</span>
              <span>₹{bookingDetail?.discount_amount || 0}</span>
            </div>

            <div className="detail-row">
              <span>Tax ({taxRate}%)</span>
              <span>₹{taxAmount}</span>
            </div>
            <div className="border-none detail-row">
              <span>Total</span>
              <span>₹{grandTotal}</span>
            </div>
            <div className="detail-row">
              <span>Paid Amount</span>
              <span className="text-green-700">₹{paidAmount}</span>
            </div>
            <div className="detail-row total">
              <span>Remaining Amount</span>
              <span>₹{remainingAmount}</span>
            </div>
          </Card>

          {/* payment detail */}
          <Card
            title="Payment Detail"
            className="p-0 mb-3 bg-white detail-card"
          >
            {!window.cn(bookingDetail?.payment_data) ? (
              bookingDetail?.payment_data?.map((p: any) => (
                <div key={p.id} className="mb-3 payment-row">
                  <h6 className="mb-1">
                    {p?.is_advance
                      ? "Advance Payment"
                      : !p?.is_advance && !p?.is_remaining
                        ? "Full Payment"
                        : "Remaining Payment"}
                  </h6>
                  <div className="detail-row">
                    <span>Method</span>
                    <span className="flex align-items-center">
                      {p.payment_method === "Gateway"
                        ? "Online (Razorpay)"
                        : "Cash"}

                      {p.payment_method === "Cash" &&
                        p.payment_status?.toLowerCase() === "pending" &&
                        bookingDetail?.booking_status.toLowerCase() ===
                        "completed" && (
                          <div className="ml-2">
                            <Button
                              className="confirm-button"
                              tooltip="Confirm Cash Payment"
                              tooltipOptions={{ position: "top" }}
                              icon="pi pi-money-bill"
                              onClick={() => handleOpenConfirmDialog()}
                            />
                          </div>
                        )}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span>Amount Paid</span>
                    <span className="flex align-items-center">
                      {!window.cn(p.amount_paid) ? `₹${p.amount_paid}` : "-"}
                    </span>
                  </div>
                  {p.payment_method !== "Cash" && (
                    <div className="detail-row">
                      <span>Payment ID</span>
                      <span>{p.razorpay_payment_id}</span>
                    </div>
                  )}
                  <div className="detail-row">
                    <span>Payment Status</span>
                    <Tag
                      value={p.payment_status}
                      severity={
                        p.payment_status?.toLowerCase() === "success"
                          ? "success"
                          : "warning"
                      }
                      className="status-tag"
                    />
                  </div>
                </div>
              ))
            ) : (
              <p>No Payment Detail Found</p>
            )}
          </Card>

          <Card className="p-0 mb-3 bg-white detail-card">
            <div className="flex flex-column align-items-center p-3">
              <i className="pi pi-star-fill text-yellow-500 text-4xl mb-2"></i>
              <h4 className="m-0">Earning Points</h4>
              <span className="text-2xl font-bold text-yellow-600">
                {bookingDetail?.earning_points || 0}
              </span>
            </div>
          </Card>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <Dialog
        visible={showConfirmDialog}
        className="mx-3 lg:mx-0"
        style={{ width: "450px" }}
        draggable={false}
        header="Confirm Payment"
        onHide={() => setShowConfirmDialog(false)}
        modal
        footer={
          <>
            <Button
              label="No"
              icon="pi pi-times"
              className="p-button-secondary"
              onClick={() => setShowConfirmDialog(false)}
            />
            <Button
              label="Confirm"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleConfirm}
              loading={loading}
            />
          </>
        }
      >
        <p>Are you sure you want to confirm this cash payment?</p>
      </Dialog>
    </>
  );
};

export default BookingDetail;
